import _ from 'lodash';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { useLocalize, useViewerRoles } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import { Heading3 } from '@treams/treams-ui';

import { DashboardEngagementLoading } from './DashboardEngagementLoading';
import { DashboardEngagementNoDataAvailable } from './DashboardEngagementNoDataAvailable';
import EngagementMetric from './metrics/EngagementMetric';

const Column = styled(Box)`
	flex-shrink: 0;
`;

function DashboardEngagementTrend(props: {
	trends: graphql.AnalyticsEngagementSubcategoryScoreOverTime[];
}) {
	const { localize } = useLocalize();

	const trends = _.sortBy(props.trends, (trend) => localize(trend.subcategory.title).text);

	return (
		<>
			<Heading3 mb={3}>{localize(props.trends[0].subcategory.category.title).text}</Heading3>
			<Flex flexWrap="wrap" mb={5}>
				<Column width={[1, 1, 1 / 2]}>
					<Box mr={[0, 0, 2]}>
						{trends
							.filter((_, i) => i % 2 === 0)
							.map((trend) => (
								<Box mb={3} key={trend.subcategory.id}>
									<EngagementMetric
										data={trend as graphql.AnalyticsEngagementSubcategoryScoreOverTime}
									/>
								</Box>
							))}
					</Box>
				</Column>
				<Column width={[1, 1, 1 / 2]}>
					<Box ml={[0, 0, 2]}>
						{trends
							.filter((_, i) => i % 2 === 1)
							.map((trend) => (
								<Box mb={3} key={trend.subcategory.id}>
									<EngagementMetric
										data={trend as graphql.AnalyticsEngagementSubcategoryScoreOverTime}
									/>
								</Box>
							))}
					</Box>
				</Column>
			</Flex>
		</>
	);
}

function EngagementTopics(props: {
	data:
		| graphql.WorkspaceEngagementTopicsQuery
		| graphql.GroupEngagementTopicsQuery
		| graphql.EngagementOfficerTopicsQuery;
}) {
	const { localize } = useLocalize();
	const data =
		'workspaceAnalytics' in props.data
			? props.data.workspaceAnalytics
			: 'groupAnalytics' in props.data
				? props.data.groupAnalytics
				: props.data.engagementAnalytics;

	const subcategories = data.engagementSubcategoriesTrend;
	const categories = new Map(
		subcategories.map((trend) => [trend.subcategory.category.id, trend.subcategory.category]),
	);
	const grouped = _.groupBy(subcategories, (trend) => trend.subcategory.category.id);
	const groups = _.sortBy(Object.keys(grouped), (id) => localize(categories.get(id)!.title).text);

	if (!groups || groups.length === 0) {
		return <DashboardEngagementNoDataAvailable />;
	}

	return (
		<>
			{groups.map((id) => (
				<DashboardEngagementTrend
					key={id}
					trends={grouped[id] as graphql.AnalyticsEngagementSubcategoryScoreOverTime[]}
				/>
			))}
		</>
	);
}

function EngagementOfficerTopics(props: { filters: graphql.AnalyticsFilters }) {
	const query = graphql.useEngagementOfficerTopicsQuery({
		variables: {
			filters: props.filters,
		},
	});

	if (!query.data) {
		return <DashboardEngagementLoading />;
	}

	return <EngagementTopics data={query.data} />;
}

function WorkspaceTopics(props: { filters: graphql.AnalyticsFilters }) {
	const query = graphql.useWorkspaceEngagementTopicsQuery({
		variables: {
			filters: props.filters,
		},
	});

	if (!query.data) {
		return <DashboardEngagementLoading />;
	}

	return <EngagementTopics data={query.data} />;
}

function GroupTopics(props: { filters: graphql.AnalyticsFilters }) {
	const query = graphql.useGroupEngagementTopicsQuery({
		variables: {
			filters: props.filters,
		},
		skip: props.filters.groupIDs?.length === 0,
	});

	if (!query.data) {
		return <DashboardEngagementLoading />;
	}

	return <EngagementTopics data={query.data} />;
}

export default function DashboardEngagementTopics(props: {
	filters: graphql.AnalyticsFilters;
	headless?: boolean;
}) {
	const userRoles = useViewerRoles();

	if (userRoles.isEngagementOfficer) {
		return <EngagementOfficerTopics filters={props.filters} />;
	}

	if (userRoles.isWorkspaceSuperuser) {
		return <WorkspaceTopics filters={props.filters} />;
	}

	return <GroupTopics filters={props.filters} />;
}
